import { useCallback, useMemo } from 'react'
import { useSessionStorage } from 'react-use'
import clsx from 'clsx'
import { Descendant } from 'slate'

import {
  ColumnConditionValue,
  ConditionValue,
  IActionRepeaterValue,
  thousandFmtNoDecimal,
  TypographyGroupValue,
} from '@makyoapp/makyo-common'
import {
  BaseComponent,
  Button,
  CornerRadius,
  Gallery,
  GalleryType,
  getShapeClass,
  getTypographyTokenStyle,
  HoverEffectVariant,
  ImageProps,
  MaterialIcon,
  Ratio,
  RawSpacingInput,
  SearchBar,
  Skeleton,
  SkeletonGroup,
} from '@makyoapp/makyo-tw-ui'

import { CoreComponentAction, CoreComponentInfos, CoreComponentTableData } from '@/core/models/app-components/types'
import { IRow } from '@/core/models/tables/row'
import { cn } from '@/lib/cn'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useCheckVisibilityV2 } from '@/hooks/__project__/useCheckVisibilityV2'
import { useCompTableData } from '@/hooks/__project__/useCompTableData'
import { ComponentSettingKey, UserColumnFilterValue } from '@/shared/builder'
import { parseSpacing } from '@/shared/builder-utils'
import { SpacingInputValue } from '@/shared/builder/types/spacing-input'
import { MAX_ROWS_LIMIT } from '@/shared/constants'
import { useDebounceInput } from '@/shared/makyo-hooks'
import { getSafely } from '@/shared/safely'

import { useCompPagination } from '../../hooks/useCompPagination'
import { CompFilterV2 } from '../../shared/CompFilterV2'
import { CompHeadline } from '../../shared/CompHeadline'
import { CompOverlayDevmode } from '../../shared/CompOverlayDevmode'
import { CompPagination } from '../../shared/CompPagination'
import { getColBrief } from '../../utils'

export interface CompGalleryWrapperCoreProps {
  infos: CoreComponentInfos
  action: CoreComponentAction
  tableData: CoreComponentTableData
}

/**
 * CompGalleryWrapperCore
 *
 * @todo
 * - like and liked should be inside image props
 */
export const CompGalleryWrapperCore = ({
  infos: { component, settings, containerClient, detailRowIndex, detailTableId, currentParentBg },
  action: { simulateAction },
  tableData: { dbTable: table },
}: CompGalleryWrapperCoreProps) => {
  const { checkVisibility } = useCheckVisibilityV2()

  //
  const compId = component._id
  const { isDevelopmentMode, openComponentSettings } = useAppBuilderContext()

  const layout = settings[ComponentSettingKey.GalleryDesignLayout] as GalleryType
  const isCarousel = layout === 'carousel'
  const isMasonry = layout === 'masonry'

  const hoverEffect = settings[ComponentSettingKey.GalleryDesignHoverEffect]

  // ========================= data =========================

  // pagination
  const datasetLimit = settings[ComponentSettingKey.GalleryDataNumItems]
  const isLimited = datasetLimit?.enabled
  const limit = datasetLimit?.value ?? 0
  const maxPerItems = isLimited ? limit : MAX_ROWS_LIMIT
  const columnsCount = settings[ComponentSettingKey.GalleryDesignNumCols] ?? 1
  const numRows = (settings[ComponentSettingKey.GalleryDesignNumRows] as number) ?? 1

  const defaultPerPage = Math.min(columnsCount * numRows, maxPerItems)

  // pagination state
  const { pagination, setPagination } = useCompPagination({
    compId,
    defaultPerPage: isMasonry ? maxPerItems : defaultPerPage,
  })

  // data filter & settings
  const filterSetting = settings[ComponentSettingKey.GalleryDataFilter] as ColumnConditionValue | undefined
  const sortType = settings[ComponentSettingKey.GalleryDataSortType] as string
  const sortBy = settings[ComponentSettingKey.GalleryDataSortBy] as string

  // search state
  const [debouncedSearch, , , currentSearch, setCurrentSearch] = useDebounceInput('')

  // user filter state
  const [userFilter, setUserFilter] = useSessionStorage(`userFilter-${compId}`, {})

  // get initial rows
  const [{ rows: _rows, filteredRowsCount, allRowsCount }, isLoading] = useCompTableData({
    table,
    componentId: component._id,
    pagination,
    filterSetting,
    sortSetting: { sortType, sortBy },
    search: debouncedSearch,
    userFilter,
  })

  const slicedRows = useMemo(() => {
    const totalRows = (pagination.page + 1) * pagination.perPage
    const sliceOffset = totalRows > maxPerItems ? pagination.perPage - (totalRows - maxPerItems) : pagination.perPage
    return _rows.slice(0, sliceOffset)
  }, [pagination, _rows, maxPerItems])

  const rows = slicedRows

  const totalPages = useMemo(() => {
    return Math.ceil(filteredRowsCount / pagination.perPage)
  }, [filteredRowsCount, pagination.perPage])

  const imageCol = getColBrief(table, settings[ComponentSettingKey.GalleryDataImage])
  const captionCol = getColBrief(table, settings[ComponentSettingKey.GalleryDataCaption])
  const avatarCol = getColBrief(table, settings[ComponentSettingKey.GalleryDataAvatar])
  const reactionCol = getColBrief(table, settings[ComponentSettingKey.GalleryDataLike])
  const tagCol = getColBrief(table, settings[ComponentSettingKey.GalleryDataTag])

  // user (manual) filter settings
  const userFilterSetting: Array<UserColumnFilterValue> = useMemo(() => {
    let stg = settings[ComponentSettingKey.GalleryBehaviorUserFilter] as UserColumnFilterValue[]
    return Array.isArray(stg) ? stg : []
  }, [settings])

  // groupBy
  const groupBy = settings[ComponentSettingKey.GalleryDataGroupBy] as string

  // map groupBy to entries using object.groupBy
  const groupEntries: [string, IRow[]][] | undefined = useMemo(() => {
    if (!groupBy) return
    const groupByCol = table.columnsMap.get(groupBy)
    if (!groupByCol) return

    return Object.entries(Object.groupBy(rows, (item) => item[groupByCol._id].toString()) as Record<string, IRow[]>)
    //
  }, [groupBy, rows, table.columnsMap])

  // ========================= design =========================

  // base
  const width = getSafely(settings, [ComponentSettingKey.GalleryDesignWidth], ['100%'])[0]
  const alignment = settings[ComponentSettingKey.GalleryDesignCompHAlign] ?? 'parent'
  const margin = settings[ComponentSettingKey.GalleryDesignMargin] as RawSpacingInput

  // spacing
  const getGap = settings[ComponentSettingKey.GalleryDesignGap] as any
  const gap = getGap ? getGap.value || 8 : 8

  const showCarouselArrow = Boolean(settings[ComponentSettingKey.GalleryDesignCarouselArrow])
  const showCarouselDots = Boolean(settings[ComponentSettingKey.GalleryDesignCarouselIndicator])

  // alignments
  const itemsAlign = settings[ComponentSettingKey.GalleryDesignCompItemsAlign] ?? 'left'
  const groupHeaderAlign = settings[ComponentSettingKey.GalleryDesignGroupHeaderAlign] || 'center'
  const searchAlign = settings[ComponentSettingKey.GalleryDesignSearchAlignment] ?? 'center'
  const paginationAlign = settings[ComponentSettingKey.GalleryDesignPaginationAlign] ?? 'center'

  // corner radius
  const imageCornerRadius = settings[ComponentSettingKey.GalleryDesignImageShape] as CornerRadius
  const avatarCornerRadius = settings[ComponentSettingKey.GalleryDesignAvatarShape] as CornerRadius

  const likeIcon = settings[ComponentSettingKey.GalleryDesignLikeIcon] as string

  // typography
  const typoSettings = settings[ComponentSettingKey.GalleryDesignTypography] as TypographyGroupValue
  const captionTypography = typoSettings[ComponentSettingKey.GalleryDesignCaptionTypography]
  const tagTypography = typoSettings[ComponentSettingKey.GalleryDesignTagTypography]

  // group header
  const groupHeaderTypo = typoSettings[ComponentSettingKey.GalleryDesignGroupHeaderTypography]
  const groupHeaderMargin = parseSpacing(
    settings[ComponentSettingKey.GalleryDesignGroupHeaderMargin] as SpacingInputValue
  )

  const groupHeaderStyles = {
    ...getTypographyTokenStyle(groupHeaderTypo),
    ...groupHeaderMargin,
    textAlign: groupHeaderAlign,
  }

  // search box
  const searchBgColor = settings?.[ComponentSettingKey.GalleryDesignSearchBgColor] ?? 'primary-90'
  const searchCornerRadius = settings[ComponentSettingKey.GalleryDesignSearchCorner] as CornerRadius
  const searchMargin = settings[ComponentSettingKey.GalleryDesignSearchMargin] as RawSpacingInput
  const searchWidth = settings[ComponentSettingKey.GalleryDesignSearchWidth]?.[0] ?? '100%'
  const searchWidthValue = Number(String(searchWidth).replace(/[^0-9.]/g, '')) || 100
  const searchWidthUnit = String(searchWidth).replace(/[0-9.]/g, '') || '%'

  // filter
  const filterOverlayBgColor = settings[ComponentSettingKey.GalleryDesignFilterBgColor] ?? 'primary-95'
  const filterChipColor = settings[ComponentSettingKey.GalleryDesignFilterChipColor] ?? 'primary-95'
  const filterCornerRadius = settings[ComponentSettingKey.GalleryDesignFilterCornerRadius] as CornerRadius
  const filterPosition = settings[ComponentSettingKey.GalleryBehaviorUserFilterPosition] ?? 'top'

  const aspectRatio = getSafely(settings, [ComponentSettingKey.GalleryDesignAspectRatio], '1:1')
  const ratio = aspectRatio ? (aspectRatio.replace(':', '/') as Ratio) : '1/1'

  // Pagination layout
  const isShowPagination = settings[ComponentSettingKey.GalleryDesignPagination] ?? true

  // LAYOUT
  const columns = settings?.[ComponentSettingKey.GalleryDesignNumCols]

  // add button
  const addButtonColor = settings[ComponentSettingKey.GalleryDesignAddButtonColor] ?? 'primary-20'
  const addButtonCornerRadius = settings[ComponentSettingKey.GalleryDesignAddButtonCornerRadius] as CornerRadius
  const addButtonPosition = settings[ComponentSettingKey.GalleryBehaviorAddButtonPosition] ?? 'inline'

  // ========================= actions =========================
  const onItemClickActionSetting = settings[ComponentSettingKey.GalleryActionItemClick] as
    | IActionRepeaterValue
    | IActionRepeaterValue[]
    | undefined

  const handleItemClick = useCallback(
    (rowId: string) => {
      openComponentSettings(component)
      if (isDevelopmentMode) return
      const onItemClickActions = onItemClickActionSetting
        ? Array.isArray(onItemClickActionSetting)
          ? onItemClickActionSetting
          : [onItemClickActionSetting]
        : []

      if (onItemClickActions?.length > 0) {
        simulateAction({
          actions: onItemClickActions,
          tableId: table.settings._id,
          rowId,
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [component, isDevelopmentMode, onItemClickActionSetting, simulateAction, table.settings._id]
  )

  const handleAddButtonClick = () => {
    simulateAction({
      actions: settings[ComponentSettingKey.GalleryActionAddButtonClick] ?? [],
      tableId: table.id,
    })
  }

  // ========================= behavior =========================
  const withSearch = settings[ComponentSettingKey.GalleryBehaviorUserSearch] as boolean
  const withSearchResultsInfo = settings[ComponentSettingKey.GalleryBehaviorSearchResultsInfo] as boolean

  const withAddButton = settings[ComponentSettingKey.GalleryBehaviorAddButtonShow] as boolean
  const addButtonVisibility = settings[ComponentSettingKey.GalleryBehaviorAddButtonVisibility] as
    | ConditionValue
    | undefined

  const showAddButton =
    withAddButton &&
    checkVisibility({
      visibility: addButtonVisibility,
      layerTableId: table.id,
    })

  // ========================= component title / headline =========================
  const headline = (settings[ComponentSettingKey.GalleryDataCompTitle] as Descendant[]) ?? []
  const headlineTypo = typoSettings[ComponentSettingKey.GalleryDesignCompTitleTypography]
  const headlineMargin = settings[ComponentSettingKey.GalleryDesignHeadlineMargin]
  const compTitleAlign = settings?.[ComponentSettingKey.GalleryDesignCompTitleAlign] as 'start' | 'center' | 'end'

  // ========================= elements =========================

  const showPagination = !isCarousel && totalPages > 1 && filteredRowsCount > pagination.perPage && isShowPagination

  // ========================= render =========================
  const renderItems = useCallback(
    (rowItems: IRow[]) => {
      return rowItems.map((row) => {
        return {
          src: getSafely(row, [getSafely(imageCol, ['_id'], '')], '').toString(),
          alt: getSafely(row, [getSafely(captionCol, ['_id'], '')], '').toString(),
          thumb: getSafely(row, [getSafely(avatarCol, ['_id'], '')], '').toString(),
          tag: getSafely(row, [getSafely(tagCol, ['_id'], '')], '').toString(),
          caption: getSafely(row, [getSafely(captionCol, ['_id'], '')], '').toString(),
          width: getSafely(row, [getSafely(imageCol, ['_id'], '')], '').toString(),
          withLike: !!getSafely(row, [getSafely(reactionCol, ['_id'], '')], '').toString(),
          liked: getSafely(row, [getSafely(reactionCol, ['_id'], ''), 'value'], false),
          likeIcon: likeIcon,
          ratio,
          captionTypography: captionTypography,
          cornerRadius: imageCornerRadius,
          avatarCornerRadius: avatarCornerRadius,
          tagTypography: tagTypography,
          onClick: () => handleItemClick(row._id),
        } as ImageProps
      })
    },
    [
      imageCol,
      captionCol,
      avatarCol,
      tagCol,
      reactionCol,
      likeIcon,
      ratio,
      captionTypography,
      imageCornerRadius,
      avatarCornerRadius,
      tagTypography,
      handleItemClick,
    ]
  )

  // if there are no rows after filtering, don't show the component
  const noDataToShow = !!filterSetting && allRowsCount === 0
  if (noDataToShow) return null

  const showSearchResultsInfo = withSearchResultsInfo && !isLoading && withSearch && debouncedSearch.trim() !== ''

  const render = (
    <BaseComponent
      id={compId}
      width={width}
      alignment={alignment}
      margin={margin}
      backgroundColor={currentParentBg}
      className={clsx('box-border')}
    >
      <CompHeadline
        containerClient={containerClient}
        detailRowIndex={detailRowIndex}
        detailTableId={detailTableId}
        headline={headline}
        headlineMargin={headlineMargin}
        headlineTypo={headlineTypo}
        compTitleAlign={compTitleAlign}
        backgroundColor={currentParentBg}
        colorType='adaptive'
      />

      {filterPosition === 'top' && (
        <CompFilterV2
          id={compId}
          value={userFilter}
          setValue={setUserFilter}
          settings={userFilterSetting}
          table={table}
          filterChipColor={filterChipColor}
          filterOverlayBgColor={filterOverlayBgColor}
          filterCornerRadius={filterCornerRadius}
          filterMargin={settings[ComponentSettingKey.GalleryDesignFilterMargin]}
          inline={false}
        />
      )}

      <div className='flex items-center gap-2 mb-4'>
        {withSearch && (
          <SearchBar
            value={currentSearch}
            onChange={(e) => {
              setCurrentSearch(e.target.value)
              setPagination(0, pagination.perPage)
            }}
            onClear={() => {
              setCurrentSearch('')
            }}
            backgroundColor={searchBgColor}
            customWidth={{
              type: searchWidthUnit === '%' ? 'percentage' : 'pixel',
              value: searchWidthValue,
            }}
            // margin={searchMargin}
            borderStyles={{
              radius: searchCornerRadius,
            }}
            align={searchAlign}
            inputClassName='py-2.5'
          />
        )}

        <div className='flex min-w-0 items-center gap-1.5'>
          {filterPosition === 'inline' && (
            <CompFilterV2
              id={compId}
              value={userFilter}
              setValue={setUserFilter}
              settings={userFilterSetting}
              table={table}
              filterChipColor={filterChipColor}
              filterOverlayBgColor={filterOverlayBgColor}
              filterCornerRadius={filterCornerRadius}
              filterMargin={{ top: 0, bottom: 0, left: 0, right: 0 }}
              inline={true}
            />
          )}

          {showAddButton && addButtonPosition === 'inline' && (
            <button
              className={clsx(
                'p-2 min-h-10 min-w-10 flex items-center justify-center cursor-pointer outline-none ring-0 focus:ring-0 focus:outline-none',
                addButtonColor === currentParentBg && `border border-on-${addButtonColor}/50`,
                getShapeClass(addButtonCornerRadius),
                `bg-${addButtonColor} text-on-${addButtonColor} hover:bg-${addButtonColor}/90`
              )}
              onClick={handleAddButtonClick}
            >
              <MaterialIcon icon='add' size={20} className={`text-on-${addButtonColor}`} />
            </button>
          )}
        </div>
      </div>

      {showSearchResultsInfo && (
        <div
          className={cn('px-1 mb-4 text-sm', `text-on-${currentParentBg}/80`)}
        >{`Showing ${thousandFmtNoDecimal.format(filteredRowsCount)} results of ${thousandFmtNoDecimal.format(allRowsCount)}`}</div>
      )}

      {isLoading ? (
        <SkeletonGroup backgroundColor={currentParentBg!}>
          <div
            className='grid gap-4'
            style={{
              gridTemplateColumns: `repeat(${columnsCount}, 1fr)`,
            }}
          >
            {[...new Array(columnsCount * Math.min(numRows, 2))].map((_, i) => (
              <Skeleton
                key={i}
                className='w-full rounded-md'
                style={{ aspectRatio: (ratio as any) === 'auto' ? '1/1' : ratio }}
              />
            ))}
          </div>
        </SkeletonGroup>
      ) : groupEntries ? (
        <div className='flex flex-col gap-4'>
          {groupEntries.map(([key, rows]) => {
            return (
              <div key={key} className='flex flex-col gap-4'>
                <div style={groupHeaderStyles}>{key}</div>
                <Gallery
                  type={layout}
                  images={renderItems(rows)}
                  columns={columns}
                  tagTypography={tagTypography.typography}
                  captionTypography={captionTypography.typography}
                  pageBackgroundColor={currentParentBg}
                  gap={gap}
                  align={itemsAlign}
                  hoverEffect={hoverEffect as HoverEffectVariant}
                  hoverIntensity='md'
                  carouselProps={{
                    showNavigation: showCarouselArrow,
                    showDots: showCarouselDots,
                    loop: true,
                    backgroundColor: currentParentBg,
                  }}
                />
              </div>
            )
          })}
        </div>
      ) : (
        <Gallery
          type={layout}
          images={renderItems(rows)}
          columns={columns}
          tagTypography={tagTypography.typography}
          captionTypography={captionTypography.typography}
          pageBackgroundColor={currentParentBg}
          gap={gap}
          align={itemsAlign}
          hoverEffect={hoverEffect as HoverEffectVariant}
          hoverIntensity='md'
          carouselProps={{
            showNavigation: showCarouselArrow,
            showDots: showCarouselDots,
            loop: true,
            backgroundColor: currentParentBg,
          }}
        />
      )}

      {showAddButton && addButtonPosition === 'bottom' && (
        <div className='mt-4'>
          <Button
            colorVariant={addButtonColor}
            fullWidth
            onClick={handleAddButtonClick}
            icon={<MaterialIcon icon='add' size={20} />}
          >
            Add Item
          </Button>
        </div>
      )}

      {showPagination && (
        <div className={clsx('flex w-full py-4 pagination')} style={{ justifyContent: paginationAlign }}>
          <CompPagination
            totalPages={totalPages}
            perPage={pagination.perPage}
            page={pagination.page}
            onChange={setPagination}
            backgroundColor={currentParentBg}
          />
        </div>
      )}
    </BaseComponent>
  )

  const handleClick = () => {
    openComponentSettings(component)
    if (isDevelopmentMode) return
  }

  if (isDevelopmentMode) {
    return (
      <div className='relative flex flex-col w-full border border-dashed' style={{ borderColor: '#333' }}>
        {render}
        <CompOverlayDevmode handleClick={handleClick} />
      </div>
    )
  }

  return render
}
